import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import MultiSelect from 'lib/MultiSelect';
import Chip, { Color, Width } from 'lib/Chip';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import { Tag } from 'components/common/types/Deal.types';
import { PopupPosition } from 'lib/Popup';
import { Store } from 'components/common/types/Store.types';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { getCurrentLineEnvironments } from 'store/dealManagement/selectors/getCurrentLineEnvironments';
import BaseSelect from 'lib/BaseSelect';
import { TagsContentProps } from './TagsContent.types';
import { CodeNameModel } from '../types';
import { TagCategory } from '../types/DirectSalesCampaign.types';

const TagsContent = ({
  onCategorySelection,
  readOnly,
  tagCategory,
  tagsData = [],
}: TagsContentProps): React.ReactElement => {
  const dispatch = useDispatch();
  const tags = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.tags);
  const environments = useSelector(getCurrentLineEnvironments, shallowEqual);

  const onTagSelection = (newTags: Tag[]): void => {
    dispatch(changeDealCurrentLineData({ tags: [...newTags] }));
  };

  const onTagDeletion = (tag?: CodeNameModel): void => {
    if (!tag) {
      dispatch(changeDealCurrentLineData({ tags: [] }));
      return;
    }

    const remainingTags = tags.filter(({ code }) => code !== tag.code);
    dispatch(changeDealCurrentLineData({ tags: remainingTags }));
  };

  const onTagIncludeExclude = (updatedItem: CodeNameModel): void => {
    const modifiedTags = tags.map((tag) =>
      tag.code === updatedItem.code ? { ...tag, include: updatedItem.include } : tag,
    );
    onTagSelection(modifiedTags);
  };

  return (
    <>
      <div className="space-y-4 w-7/12">
        <BaseSelect
          name="tags"
          dataTestId="tags"
          options={tagsData}
          selectedValue={tagCategory}
          placeholder="Select tags"
          onSelect={(_, value: CodeNameModel) => onCategorySelection(value as TagCategory)}
          isDisabled={readOnly || !environments.length || !tagsData.length}
        />
        <MultiSelect
          items={tagCategory?.tags ?? []}
          dataTestId="tag-input"
          selectedItems={tags}
          onSelect={onTagSelection}
          onClearAll={() => onTagDeletion()}
          isDisabled={readOnly || !tagCategory}
          containerPosition={PopupPosition.TOP}
        />
      </div>
      <div className="space-y-4 w-7/12" data-test-id="tags-list-chips">
        {tags?.map((tag) => (
          <div key={tag.code} aria-label={tag.name} className="flex justify-between items-center">
            <Chip
              label={tag.name}
              color={Color.PRIMARY}
              width={Width.FULL}
              truncateLabel={false}
              isDisabled={readOnly}
              onRemove={() => onTagDeletion(tag)}
            />
            <IncludeExcludeToggle
              option={tag}
              onIncludeExcludeChange={onTagIncludeExclude}
              isAlwaysVisible
              isSelected={tag.include !== undefined}
              isOptionNameSelectable={false}
              isHovered={false}
              isDeselectAllowed={false}
              isDisabled={readOnly}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default TagsContent;
